import { string } from 'yup'
import { useEffect } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/input'
import { Field, ErrorMessage, useFormikContext } from 'formik'

import { FormValues } from '..'
import { Link } from '@components/Link'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'
import { InvalidMessage } from '@components/UI/Form/InvalidMessage'
import styles from '../../../UI/Form/Input/Input.module.css'
import { useRouter } from 'next/router'

interface SignupFormProps {
  userType?: string
  openLoginModal: () => void
}

export function SignupForm(props: SignupFormProps) {
  const { query } = useRouter()
  const {
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    validateField
  } = useFormikContext<FormValues>()

  useEffect(() => {
    const viewToken = query.newUserWithViewPrivToken as string
    if (viewToken) {
      setFieldValue(
        'signup.email',
        JSON.parse(atob(viewToken.split('.')[1])).user_email
      )
    }
  }, [query])

  function handleBlur(e) {
    setFieldTouched('signup.phoneNumber', true)
    validateField('signup.phoneNumber')
    if (!e.target.value) {
      setFieldValue('signup.phoneNumber', undefined)
    }
  }

  return (
    <>
      <p className="mb-8">
        Already have an account?{' '}
        <a
          className="text-brand hover:underline cursor-pointer"
          onClick={props.openLoginModal}
        >
          Log in
        </a>
      </p>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-8 md:gap-y-4 mb-8">
          <div>
            <Label required htmlFor="signup.firstName">
              First Name
            </Label>
            <Field
              id="signup.firstName"
              component={Input}
              name="signup.firstName"
              placeholder="First"
              validate={(name: string) => {
                if (!name) return 'Please provide a first name'
              }}
            />
          </div>
          <div>
            <Label required htmlFor="signup.lastName">
              Last Name
            </Label>
            <Field
              id="signup.lastName"
              component={Input}
              name="signup.lastName"
              placeholder="Last"
              validate={(name: string) => {
                if (!name) return 'Please provide a last name'
              }}
            />
          </div>
          <div>
            <Label required htmlFor="signup.phoneNumber">
              Phone Number
            </Label>
            <Field
              id="signup.phoneNumber"
              component={PhoneInput}
              onChange={(value: string) =>
                setFieldValue('signup.phoneNumber', value)
              }
              className={
                styles.input +
                (touched.signup?.phoneNumber && errors.signup?.phoneNumber
                  ? ` ${styles.error}`
                  : '')
              }
              country="US"
              name="signup.phoneNumber"
              placeholder="(123) 456-7890"
              onBlur={handleBlur}
              validate={(phone: string) => {
                if (!phone) {
                  return 'Please provide a phone number'
                } else if (!isValidPhoneNumber(phone)) {
                  return 'Please provide a valid phone number'
                }
              }}
            />
            <ErrorMessage
              component={InvalidMessage}
              name="signup.phoneNumber"
            />
          </div>
          <div>
            <Label required htmlFor="signup.email">
              Email
            </Label>
            <Field
              id="signup.email"
              component={Input}
              name="signup.email"
              placeholder="firstlast@example.com"
              validate={async (email: string) => {
                if (!email) return 'Please provide an email address'
                else if (!(await string().email().isValid(email))) {
                  return 'Please provide a valid email address'
                }
              }}
            />
          </div>
          <div>
            <Label required htmlFor="signup.password">
              Password
            </Label>
            <Field
              id="signup.password"
              component={Input}
              type="password"
              name="signup.password"
              validate={(password: string) => {
                if (!password) return 'Please provide a password'
              }}
            />
          </div>
          <div>
            <Label required htmlFor="signup.confirmPassword">
              Confirm Password
            </Label>
            <Field
              id="signup.confirmPassword"
              component={Input}
              type="password"
              name="signup.confirmPassword"
              validate={(password: string) => {
                if (!password) return 'Please confirm your password'
              }}
            />
          </div>
        </div>
        {!props.userType && (
          <div>
            <Label required htmlFor="signup.type" className="mr-12">
              I am a(n)...
            </Label>
            <div
              className={`flex flex-col space-y-4 py-2 rounded md:space-y-0 md:flex-row md:space-x-8${
                touched.signup?.type && errors.signup?.type ? ' bg-red-50' : ''
              }`}
            >
              <div className="flex items-center">
                <Label>
                  <Field
                    type="radio"
                    name="signup.type"
                    value="BROKER"
                    className="mr-2"
                  />
                  Agent
                </Label>
              </div>
              <div className="flex items-center">
                <Label>
                  <Field
                    type="radio"
                    name="signup.type"
                    value="INSPECTOR"
                    className="mr-2"
                  />
                  Inspector
                </Label>
              </div>
              <div className="flex items-center">
                <Label>
                  <Field
                    type="radio"
                    name="signup.type"
                    value="CLIENT"
                    className="mr-2"
                  />
                  Homeowner/Home Buyer
                </Label>
              </div>
            </div>
          </div>
        )}

        <ErrorMessage component={InvalidMessage} name="signup.type" />
        <div
          className={`flex items-center mt-4 rounded py-2 ${
            touched.signup?.acceptTOC && errors.signup?.acceptTOC
              ? ' bg-red-50'
              : ''
          }`}
        >
          <Field
            type="checkbox"
            name="signup.acceptTOC"
            id="signup.acceptTOC"
            className="mr-4"
            data-testid="termsCheckbox"
          />
          <Label required htmlFor="signup.acceptTOC">
            I agree to Majordomo's{' '}
            <Link type="outbound" href="https://majordomo.com/terms-of-service">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link type="outbound" href="https://majordomo.com/privacy-policy">
              Privacy Policy
            </Link>
          </Label>
        </div>
        <ErrorMessage component={InvalidMessage} name="signup.acceptTOC" />

        {props.userType === 'inspector' && (
          <div>
            <div
              className={`flex items-center mt-4 rounded py-2 ${
                touched.signup?.acceptAffiliateTOC &&
                errors.signup?.acceptAffiliateTOC
                  ? ' bg-red-50'
                  : ''
              }`}
            >
              <Field
                type="checkbox"
                name="signup.acceptAffiliateTOC"
                id="signup.acceptAffiliateTOC"
                className="mr-4"
                data-testid="affiliateTermsCheckbox"
              />
              <Label required htmlFor="signup.acceptAffiliateTOC">
                I agree to Majordomo’s{' '}
                <a
                  href="/inspector-affiliate-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand hover:underline"
                >
                  Affiliate Program Terms of Service
                </a>
              </Label>
            </div>
            <ErrorMessage
              component={InvalidMessage}
              name="signup.acceptAffiliateTOC"
            />
          </div>
        )}
      </div>
    </>
  )
}
